import React, { useState, useEffect, useRef } from 'react';
import './Navbar.css';
import { Link, useLocation } from 'react-router-dom';
import { Container, Button } from 'reactstrap';
import { FaBars, FaTimes } from 'react-icons/fa';
import logo from '../../../assets/images/logos/mcgp-logo.png';
import pdf from '../../../assets/whitepaper.pdf';

function MainNavigation() {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('');
  const [hoverItem, setHoverItem] = useState(null);
  const navRef = useRef(null);

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);

      // Calculate scroll progress
      const totalHeight =
        document.documentElement.scrollHeight -
        document.documentElement.clientHeight;
      const progress = (window.scrollY / totalHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Set active tab based on location
  useEffect(() => {
    setActiveTab(location.pathname);
    // Close mobile menu when route changes
    setIsOpen(false);
  }, [location]);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isOpen && navRef.current && !navRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Prevent scroll when mobile menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'auto';
    }
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  // Add animation for logo
  useEffect(() => {
    const logoElement = document.querySelector('.brand-logo');
    if (logoElement) {
      logoElement.classList.add('logo-animation');
    }
  }, []);

  // Toggle mobile menu
  const toggle = () => setIsOpen(!isOpen);

  // Handle link clicks
  const handleLinkClick = () => {
    setIsOpen(false);
  };

  const handleMouseEnter = (item) => {
    setHoverItem(item);
  };

  const handleMouseLeave = () => {
    setHoverItem(null);
  };

  // Nav items with descriptions for hover effect
  const navItems = [
    { path: '/', text: 'Home', description: 'Back to main page' },
    {
      path: '/otc',
      text: 'OTC',
      description: 'Buy & Sell MCGP',
    },
    {
      path: '#solutions',
      text: 'Solutions',
      description: 'Our blockchain solutions',
    },
    {
      path: '#community',
      text: 'Community',
      description: 'Join our community',
    },
    {
      path: pdf,
      text: 'Whitepaper',
      description: 'Read our documentation',
      download: true,
    },
  ];

  return (
    <header
      className={`main-header ${isScrolled ? 'scrolled' : ''}`}
      ref={navRef}
    >
      <div className="scroll-progress-container">
        <div
          className="scroll-progress-bar"
          style={{ width: `${scrollProgress}%` }}
        ></div>
      </div>

      <div
        className={`navbar-overlay ${isOpen ? 'visible' : ''}`}
        onClick={toggle}
      ></div>
      <Container className="header-container">
        <div className="navbar-main">
          <Link to="/" className="navbar-brand">
            <img src={logo} alt="MCGPChain Logo" className="brand-logo" />
          </Link>

          <div
            className={`nav-toggle ${isOpen ? 'open' : ''}`}
            onClick={toggle}
          >
            {isOpen ? (
              <FaTimes className="toggle-icon" />
            ) : (
              <FaBars className="toggle-icon" />
            )}
          </div>

          <nav className={`navbar-menu ${isOpen ? 'open' : ''}`}>
            <ul className="navbar-nav">
              {navItems.map((item) => (
                <li
                  key={item.path}
                  className="nav-item"
                  onMouseEnter={() => handleMouseEnter(item.text)}
                  onMouseLeave={handleMouseLeave}
                >
                  {item.download ? (
                    <a
                      href={item.path}
                      className="nav-link"
                      download="Whitepaper.pdf"
                      onClick={handleLinkClick}
                    >
                      {item.text}
                      {hoverItem === item.text && (
                        <span className="nav-tooltip">{item.description}</span>
                      )}
                    </a>
                  ) : (
                    <Link
                      to={item.path}
                      className={
                        activeTab === item.path ? 'nav-link active' : 'nav-link'
                      }
                      onClick={handleLinkClick}
                    >
                      {item.text}
                      {hoverItem === item.text && (
                        <span className="nav-tooltip">{item.description}</span>
                      )}
                    </Link>
                  )}
                </li>
              ))}
            </ul>

            <div className="navbar-buttons">
              <Link to="/presale" onClick={handleLinkClick}>
                <Button className="btn btn-primary nav-button-animated">
                  Buy $MCGP
                </Button>
              </Link>
            </div>
          </nav>
        </div>
      </Container>
    </header>
  );
}

export default MainNavigation;
